import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { nth, map, filter, get, isEmpty, take } from 'lodash'
import moment from 'moment'
import { toast } from 'react-toastify'

import Environment from '../utils/environment'
import { isDuringBusinessDate } from '../utils/businessHours'

import { fetchAttendants, attemptPinLogin, clearAttemptPinLoginStatus, clearFetchAttendantsStatus } from '../actions/attendant'
import { loadRevenueCenters } from '../actions/revenueCenter'
import { fetchSalesEvents, fetchSalesEventTypes } from '../actions/suites/salesEvent'
import { cleanupState } from '../actions/shared'
import { fetchEmployeeRoles } from '../actions/employeeRole'

import { getAttemptPinLoginStatus, getFetchAttendantsStatus } from '../selectors/attendant'
import { getAttendantSettingsError } from '../selectors/attendantSettings'
import { makeGetEvents, getEventsLoading } from '../selectors/events'
import { getVenueName } from '../selectors/venue'
import { getIsSuitesMode, getIsSuitesOrTabsMode } from '../selectors/config'
import { getEmployeeApprovalCodes } from '../selectors/employeeRole'

import useNumberPad from '../hooks/useNumberPad'
import useValidateEmployeePin from '../hooks/useValidateEmployeePin'

import UserLoginComponent from '../components/userLogin/Component'
import NavMenu from '../components/NavMenu'
import { menusRequested } from '../actions/menus'


const UserLogin = ({ venueName, events, eventsLoading, refreshEvents, pinLoginStatus, pinLoginStatusText, loadRevenueCenters, onSubmit, onSuccess, onMount, onUnmount, isSuites, approvalCodes, fetchAttendantsStatus }) => {
  const minLength = 4
  const maxLength = 5
  const [
    output,
    onInput,
    isValid,
  ] = useNumberPad(
    '',
    (value) => value,
    (value) => value.length >= minLength && value.length <= maxLength,
    maxLength,
    ''
  )
  const requestApproval = useValidateEmployeePin()
  const [showMenu, setShowMenu] = useState(false)

  const onMenuToggle = () => {
    setShowMenu(!showMenu)
  }

  const handleRefreshEvents = () => {
    if (eventsLoading) return

    refreshEvents()
  }

  const handleSubmit = (selectedEventIndex) => {
    let eventId
    if (isSuites) {
      const event = nth(events, selectedEventIndex)
      eventId = event?.uuid
    }
    if (fetchAttendantsStatus === 'loading') {
      toast.error('Attendants are still being fetched. Please try again.')
    } else if (!isValid) {
      toast.error('Pin must be at least 4 characters')
    } else if (!approvalCodes.includes(output)) {
      toast.error('Must be an active attendant')
    } else {
      onSubmit(output, eventId)
    }
  }

  useEffect(() => {
    onMount()

    if (isEmpty(venueName)) {
      loadRevenueCenters()
    }

    return () => {
      onUnmount()
      clearAttemptPinLoginStatus()
      clearFetchAttendantsStatus()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (pinLoginStatus === 'succeeded') {
      onSuccess()
    }

    if (pinLoginStatus === 'failed') {
      onInput('', true)
      toast.error(pinLoginStatusText)
    }
  // TODO: We need to audit the exhaustive dependencies here
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinLoginStatus])

  return (
    <>
      <UserLoginComponent
        venueName={venueName}
        eventsLoading={eventsLoading}
        events={events.map(event => ({ ...event, disabled: !isValid }))}
        handleRefreshEvents={handleRefreshEvents}
        pin={output}
        pinSize={maxLength}
        pinLoginStatus={pinLoginStatus}
        pinLoginStatusText={pinLoginStatusText}
        handlePinEntry={onInput}
        isSuites={isSuites}
        showMenu={showMenu}
        handleSubmit={handleSubmit}
        onOpenMenu={() => {
          requestApproval(output, () => {
            setShowMenu(true)
          })
        }}
      />
      {showMenu && <NavMenu onToggle={onMenuToggle} />}
    </>
  )
}

function formatEvent(event) {
  const startTimeDisplay = moment(event.startTime).format('MMM Do YYYY, h:mm A')

  return { ...event, startTimeDisplay }
}

const formatEvents = (events) => {
  if (Environment.app.isDevelopment) {
    const slicedEvents = take(events, 3)
    return map(slicedEvents, formatEvent)
  }

  const filteredEvents = filter(events, isDuringBusinessDate)
  const formattedEvents = map(filteredEvents, formatEvent)

  return formattedEvents
}

const mapStateToProps = (state, ownProps) => {
  const getEvents = makeGetEvents()
  const events = getEvents(state)
  const eventsLoading = getEventsLoading(state)
  const pinLoginStatus = getAttemptPinLoginStatus(state)
  const errorMessage = getAttendantSettingsError(state)
  const venueName = getVenueName(state)
  const isSuites = getIsSuitesMode(state)
  const isSuitesOrTabs = getIsSuitesOrTabsMode(state)
  const approvalCodes = getEmployeeApprovalCodes(state)
  const fetchAttendantsStatus = getFetchAttendantsStatus(state)

  const PIN_LOGIN_STATUS_TEXT_MAP = {
    loading: 'Requesting Access',
    succeeded: 'Access Granted',
    failed: errorMessage,
  }

  const pinLoginStatusText = PIN_LOGIN_STATUS_TEXT_MAP[pinLoginStatus]
  const formattedEvents = formatEvents(events)

  return { venueName, eventsLoading, events: formattedEvents, pinLoginStatusText, pinLoginStatus, isSuites, isSuitesOrTabs, approvalCodes, fetchAttendantsStatus }
}

const mapDispatchToProps = (dispatch, ownProps) => {
  const replace = get(ownProps, 'history.replace', () => {})
  const attendantsAlreadyFetched = ownProps?.history?.location?.state?.attendantsAlreadyFetched ?? false

  return {
    loadRevenueCenters: () => dispatch(menusRequested()),
    refreshEvents: () => {
      dispatch(fetchSalesEvents())
      dispatch(fetchSalesEventTypes())
    },
    onMount: () => {
      dispatch(clearAttemptPinLoginStatus())
      dispatch(fetchSalesEvents())
      dispatch(fetchSalesEventTypes())
      dispatch(cleanupState())
      dispatch(fetchEmployeeRoles())
      if (!attendantsAlreadyFetched) {
        dispatch(fetchAttendants())
      }
    },
    onUnmount: () => {
      dispatch(clearAttemptPinLoginStatus())
      dispatch(clearFetchAttendantsStatus())
    },
    onSubmit: (pin, eventId) => {
      dispatch(attemptPinLogin(pin, eventId))
    },
    onSuccess: (isSuitesOrTabs) => {
      if (isSuitesOrTabs) {
        replace('/tabs')
      } else {
        replace('/')
      }
    },

  }
}

export default connect(mapStateToProps, mapDispatchToProps)(UserLogin)
